import { useEffect, useState } from 'react';
import { Check, Download, LoaderCircle, Trash2 } from 'lucide-react';
import { Button } from './ui';
import { downloadLecture, getOfflineLecture, removeOfflineLecture } from '../lib/offlineDownloads';

type Props = {
  id: string;
  title: string;
  description?: string;
  sourceUrl: string;
  duration?: number;
  thumbnail?: string;
  compact?: boolean;
};

export function OfflineDownloadButton({ compact = false, ...lecture }: Props) {
  const [downloaded, setDownloaded] = useState(false);
  const [progress, setProgress] = useState<number>();
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void getOfflineLecture(lecture.id).then((value) => { if (active) setDownloaded(Boolean(value)); });
    return () => { active = false; };
  }, [lecture.id]);

  const toggle = async () => {
    setError('');
    if (downloaded) {
      await removeOfflineLecture(lecture.id);
      setDownloaded(false);
      return;
    }
    setProgress(0);
    try {
      await downloadLecture(lecture, setProgress);
      setDownloaded(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Download could not be saved.');
    } finally {
      setProgress(undefined);
    }
  };

  if (progress !== undefined) {
    return <span className="download-progress" aria-label={`Downloading ${progress}%`}><LoaderCircle size={15} className="spin" /> {progress}%</span>;
  }
  return <span className="download-action">
    <Button variant={downloaded ? 'soft' : 'ghost'} icon={downloaded ? Check : compact ? Download : Download} onClick={(event) => { event.preventDefault(); event.stopPropagation(); void toggle(); }} aria-label={downloaded ? `Remove ${lecture.title} download` : `Download ${lecture.title} for offline use`}>
      {compact ? '' : downloaded ? 'Downloaded' : 'Download'}
    </Button>
    {downloaded && compact && <button className="download-remove" onClick={(event) => { event.preventDefault(); event.stopPropagation(); void toggle(); }} aria-label={`Remove ${lecture.title} download`}><Trash2 size={14} /></button>}
    {error && <small className="download-error">{error}</small>}
  </span>;
}
